import { createContext, useContext, useState, useCallback, useMemo, useEffect, type ReactNode } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { useDetachedPanels } from '../hooks/useDetachedPanels';
import type { UseDetachedPanelsReturn } from '../hooks/useDetachedPanels';
import type { WorkspaceMode, SidebarConfig, UIState, SidebarTabId } from '../types';
import { DEFAULT_UI_STATE, WORKSPACE_PRESETS, LEGACY_TAB_FALLBACK, migrateSidebarWidth } from '../types';
import { useLayoutContext } from './LayoutContext';

export interface WorkspaceContextValue {
  mode: WorkspaceMode;
  setMode: (mode: WorkspaceMode) => void;
  /** @deprecated Layout fields are delegated to LayoutContext */
  sidebarConfig: SidebarConfig;
  /** @deprecated Layout fields are delegated to LayoutContext */
  setSidebarConfig: (config: SidebarConfig | ((prev: SidebarConfig) => SidebarConfig)) => void;
  /** @deprecated Use LayoutContext dispatch({ type: 'layout:toggleSidebar' }) */
  toggleCollapsed: () => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  exitFullscreen: () => void;
  detachedPanels: UseDetachedPanelsReturn;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const [uiStateRaw, setUiState] = useLocalStorage<UIState>('roll1:ui-state', DEFAULT_UI_STATE);
  // Runtime fallback for removed tabs (chat, ai, initiative)
  const uiState: UIState = {
    ...uiStateRaw,
    sidebarWidth: migrateSidebarWidth(uiStateRaw.sidebarWidth),
    activeTab: (LEGACY_TAB_FALLBACK[uiStateRaw.activeTab] ?? uiStateRaw.activeTab) as SidebarTabId,
  };
  const [isFullscreen, setIsFullscreen] = useState(false);
  const detachedPanels = useDetachedPanels();
  const layout = useLayoutContext();

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen((prev) => !prev);
  }, []);

  const exitFullscreen = useCallback(() => {
    setIsFullscreen(false);
  }, []);

  const sidebarConfig: SidebarConfig = {
    width: uiState.sidebarWidth,
    collapsed: uiState.sidebarCollapsed,
    activeTab: uiState.activeTab,
  };

  const mode = uiState.workspaceMode;

  // Sync workspace layout state to LayoutContext (one-way bridge)
  useEffect(() => {
    layout.dispatch({ type: 'layout:setSidebarWidth', width: uiState.sidebarWidth });
    layout.dispatch({ type: 'layout:setSidebarTab', tab: uiState.activeTab });
    if (uiState.sidebarCollapsed) {
      // Only toggle if out of sync
      if (layout.state.sidebarOpen) {
        layout.dispatch({ type: 'layout:toggleSidebar' });
      }
    } else {
      if (!layout.state.sidebarOpen) {
        layout.dispatch({ type: 'layout:toggleSidebar' });
      }
    }
  }, [uiState.sidebarWidth, uiState.activeTab, uiState.sidebarCollapsed]); // eslint-disable-line react-hooks/exhaustive-deps

  const setMode = useCallback(
    (newMode: WorkspaceMode) => {
      const config = WORKSPACE_PRESETS[newMode];
      setUiState((prev) => ({
        ...prev,
        workspaceMode: newMode,
        sidebarWidth: config.sidebarWidth,
        activeTab: config.defaultTab,
        sidebarCollapsed: false,
      }));
      // Also dispatch to LayoutContext
      layout.dispatch({ type: 'layout:setSidebarWidth', width: config.sidebarWidth });
      layout.dispatch({ type: 'layout:setSidebarTab', tab: config.defaultTab });
      // Smart ToolOptionsBar visibility: hide in combat, show in prep/explore
      layout.dispatch({ type: 'layout:setToolOptions', visible: newMode !== 'combat' });
    },
    [setUiState, layout],
  );

  const setSidebarConfig = useCallback(
    (configOrFn: SidebarConfig | ((prev: SidebarConfig) => SidebarConfig)) => {
      setUiState((prev) => {
        const prevConfig: SidebarConfig = {
          width: prev.sidebarWidth,
          collapsed: prev.sidebarCollapsed,
          activeTab: prev.activeTab,
        };
        const next = typeof configOrFn === 'function' ? configOrFn(prevConfig) : configOrFn;
        return {
          ...prev,
          sidebarWidth: next.width,
          sidebarCollapsed: next.collapsed,
          activeTab: next.activeTab,
        };
      });
    },
    [setUiState],
  );

  const toggleCollapsed = useCallback(() => {
    setSidebarConfig((prev) => ({ ...prev, collapsed: !prev.collapsed }));
    layout.dispatch({ type: 'layout:toggleSidebar' });
  }, [setSidebarConfig, layout]);

  const value = useMemo(
    () => ({ mode, setMode, sidebarConfig, setSidebarConfig, toggleCollapsed, isFullscreen, toggleFullscreen, exitFullscreen, detachedPanels }),
    [mode, setMode, sidebarConfig, setSidebarConfig, toggleCollapsed, isFullscreen, toggleFullscreen, exitFullscreen, detachedPanels],
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspaceContext() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspaceContext must be used within WorkspaceProvider');
  return ctx;
}
